import React from 'react';

import type { Nullable } from '@clearverify/types';

import { useVerifierLogoQuery, useVerifierUpdateMutation, VerifierDocument } from 'src/api';
import { useNotification } from 'src/hooks/app';
import { DroppedFile } from 'src/types/file';

import { useFileUpload } from '../file';

export const useVerifierLogo = (verifierId: Nullable<string>) => {
  const { createApolloContext } = useNotification();

  const { uploadFile, uploading } = useFileUpload();

  const [verifierUpdateMutation, { loading: verifierLogoUpdating }] = useVerifierUpdateMutation({
    awaitRefetchQueries: true,
    refetchQueries: [VerifierDocument],
    context: createApolloContext({
      errorMessage: 'Something went wrong while updating logo',
    }),
  });

  const { data, loading: verifierLogoLoading } = useVerifierLogoQuery({
    variables: {
      id: verifierId || '',
    },
    skip: !verifierId,
  });

  const verifier = data?.verifier;
  const verifierLogo = verifier?.logo;

  const updateVerifierLogo = React.useCallback(
    async (logoFile: DroppedFile | string) => {
      // TODO: handle case with fileId
      if (typeof logoFile === 'string') throw new Error('Not implemented');
      if (!logoFile.file || !verifierId) return;

      try {
        const uploadedFile = await uploadFile(logoFile.file);
        await verifierUpdateMutation({
          variables: {
            id: verifierId,
            data: {
              logo: {
                connect: { id: uploadedFile.id },
              },
            },
          },
        });
      } catch (error) {
        console.error(error);
      }
    },
    [verifierId, verifierUpdateMutation, uploadFile],
  );

  return {
    verifierLogo,
    verifierLogoLoading,
    updateVerifierLogo,
    verifierLogoUpdating: uploading || verifierLogoUpdating,
  };
};
